/**
 * Geometry quality predicates — pure functions over composed board geometry.
 *
 * Each one answers a question a reader would ask of the drawing ("can I tell
 * which line is which", "is that line going through that box") and reports
 * MAGNITUDE, not a boolean: grazing a corner by 1px and punching 40px through
 * the middle are different defects and a ratchet that can't tell them apart is
 * a ratchet that fails on noise.
 *
 * These read the geometry that is actually STROKED — `PlannedRoute.d`, placed
 * frame labels, port pills, composed text runs — which is what separates them
 * from `board/layoutMetrics.ts`. That module measures straight chords between
 * node centres; it answers "is the SOLVE good", these answer "is the DRAWING
 * readable". Both are kept: a chord metric is stable under a routing change,
 * these are not, and that is the point.
 *
 * Some defaults below are derived from the routers' own numbers
 * (`AMBIGUOUS_DISTANCE` from `RAIL_GAP`, `RHYTHM_FLOOR` under `NECK_LEN` /
 * `CORNER_RADIUS`); the rest are free parameters, each with its reasoning at
 * the constant. None was picked to make today's boards pass — two were moved
 * BECAUSE the real corpus disproved them.
 *
 * Every threshold is in ABSOLUTE canvas px, so a node inside a subspace
 * projected at 0.4 is judged at 0.4 of the sensitivity of a root-level one.
 * That is deliberate — absolute px is what a reader's eye gets — but it does
 * mean a small nested defect can sit under the floor.
 */
import { RAIL_GAP, RHYTHM_FLOOR, RHYTHM_KINK, polylineStutters } from "../board/edgeRouting.js";
import {
  boundsOf,
  headingDelta,
  heading,
  insetRect,
  intersectionAt,
  intersectionLength,
  parallelRun,
  properIntersection,
  rectBorder,
  rectsOverlap,
  routePolyline,
  simplifyPolyline,
  type Pt,
  type Rect,
} from "./segments.js";
import { resultOf, type Finding, type PredicateResult, type SceneGeometry } from "./types.js";

/** Node-rect shrink before a route counts as running THROUGH it. Borrowed from
 *  the live sweep's own tolerance (`e2e-visual/viewSweep.spec.ts` inset = 6).
 *  The verdicts are not identical and are not meant to be: the sweep samples 11
 *  points in screen px at the current zoom and counts one crossing per path,
 *  this measures continuous penetration in board units per (route, node) pair.
 *  Sharing the constant just keeps them calibrated to the same idea of "grazing
 *  the border doesn't count". */
export const NODE_INSET = 6;
/** Below this, a "crossing" is a stroke width's worth of contact — the arrow
 *  head touching its target, a rounded corner clipping a border. */
export const MIN_PENETRATION = 3;
/** Two lines closer than this in heading read as parallel. */
export const PARALLEL_ANGLE = (5 * Math.PI) / 180;
/**
 * Nearer than this and a reader can no longer separate two parallel lines.
 *
 * Sits strictly BELOW `RAIL_GAP`, which is the separation the router draws on
 * purpose: `railPaths(center, RAIL_GAP)` offsets each leg by `gap / 2`, so a
 * mutual pair lands exactly `RAIL_GAP` apart, and sibling fan slots are spread
 * by the same 10px step. Flagging at `RAIL_GAP` would fault every designed
 * pair on the board — measured, not assumed: it did, on FullArchitecture.
 */
export const AMBIGUOUS_DISTANCE = RAIL_GAP / 2;
/** Shorter than this, a shared corridor is a crossing, not a corridor. */
export const MIN_CORRIDOR_RUN = 24;
/** A route hugging a frame border for this long stops reading as a route. */
export const MIN_BORDER_RUN = 40;
export const BORDER_TOLERANCE = 6;
/** The rhythm rule lives in `board/edgeRouting.ts` — the planner's rhythm
 *  bridge filters its candidates through the SAME constants and the SAME
 *  function this predicate judges them with. Re-exported so the quality API
 *  reads whole. */
export { RHYTHM_FLOOR, RHYTHM_KINK };
/** How far along its own route we look for a neck that dives back into the
 *  node it just left. */
export const ENDPOINT_PROBE = 24;
/** Width the artifact is assumed to be READ at. A board composed 3000px wide
 *  and printed 930px wide draws its 12px labels at under 4px. */
export const REFERENCE_WIDTH = 930;
/** Smallest legible projected text size. */
export const TEXT_FLOOR_PX = 6;

interface RouteGeom {
  readonly route: SceneGeometry["routes"][number];
  readonly pts: readonly Pt[];
  readonly simple: readonly Pt[];
  readonly bbox: Rect | null;
}

/** Flatten + simplify a report's routes. Each predicate calls this itself, so a
 *  full report re-parses the route set once per predicate — measured at 88ms
 *  for 239 routes on the 324-module stress board, which is far cheaper than the
 *  prepared-bundle plumbing that would remove it. */
function routeGeoms(geo: SceneGeometry): RouteGeom[] {
  return geo.routes.map((route) => {
    const pts = routePolyline(route.d);
    return { route, pts, simple: simplifyPolyline(pts), bbox: boundsOf(pts) };
  });
}

const grow = (r: Rect, by: number): Rect => insetRect(r, -by);

// --- 1. label ↔ route clearance -------------------------------------------

/**
 * A route running through a label box. Reported as PENETRATION LENGTH — the
 * px of line inside the box — because that is the difference between a line
 * that clips a descender and one that bisects the word.
 *
 * A port pill's own connector docks AT the pill, so that pairing is excluded
 * by `ownerRouteId`.
 */
export function labelRouteClearance(
  geo: SceneGeometry,
  opts: { readonly minPenetration?: number } = {},
): PredicateResult {
  const min = opts.minPenetration ?? MIN_PENETRATION;
  const findings: Finding[] = [];
  const geoms = routeGeoms(geo);
  for (const label of geo.labels) {
    for (const { route, pts, bbox } of geoms) {
      if (label.ownerRouteId === route.id) continue;
      if (!bbox || !rectsOverlap(bbox, label.rect)) continue;
      const len = intersectionLength(pts, label.rect);
      if (len <= min) continue;
      findings.push({
        kind: "labelRouteClearance",
        subjects: [route.id, label.id],
        magnitude: len,
        at: intersectionAt(pts, label.rect),
      });
    }
  }
  return resultOf(findings);
}

// --- 2. route through node -------------------------------------------------

/**
 * A route running through a node that is not its own endpoint. The headless
 * equivalent of the browser sweep's `measureCrossings`, and the reason this
 * module exists: that check needed `getPointAtLength` on a live DOM, so the
 * whole class was invisible to unit tests.
 *
 * A node is exempt for a given route only when the overlap is structural: it
 * IS an endpoint, it CONTAINS an endpoint (a route into a subspace member
 * legitimately crosses its host's body), or the route is drawn inside it. A
 * blanket "containers are never obstacles" rule would be wrong — a sibling
 * container standing in a port's lane is exactly the defect being looked for.
 */
/**
 * The port-connector subset of any route-keyed finding list.
 *
 * A port connector over a node is the SAME defect as an edge over a node —
 * `routeThroughNode` reports both, with the port-ness carried in the route
 * kind, which is why `render/geometry-gate.ts` refuses delivery on one code
 * and not a minted port-specific one. But that subset is the single class both
 * gates declare HARD_ZERO, so two callers select it: the delivery gate (what
 * blocks an artifact) and the solver's regauge floor (what refuses a
 * candidate). Selecting it in two places is how the delivery gate and the
 * solver come to disagree about which boards are clean — the solver passes a
 * board `archlang render` then refuses. One selector, both callers.
 */
export function portRouteFindings(
  geo: SceneGeometry,
  findings: readonly Finding[],
): readonly Finding[] {
  const kindOf = new Map(geo.routes.map((r) => [r.id, r.kind] as const));
  return findings.filter((f) => kindOf.get(f.subjects[0] ?? "") === "port");
}

export function routeThroughNode(
  geo: SceneGeometry,
  opts: { readonly inset?: number; readonly minPenetration?: number } = {},
): PredicateResult {
  const inset = opts.inset ?? NODE_INSET;
  const min = opts.minPenetration ?? MIN_PENETRATION;
  const findings: Finding[] = [];
  const obstacles = geo.nodes.map((n) => ({ node: n, rect: insetRect(n.rect, inset) }));
  for (const { route, pts, bbox } of routeGeoms(geo)) {
    if (!bbox) continue;
    const inside = (id: string): boolean => route.path === id || route.path.startsWith(id + "/");
    const holds = (id: string): boolean =>
      route.endpoints.some((e) => e === id || e.startsWith(id + "/"));
    for (const { node, rect } of obstacles) {
      if (holds(node.id) || inside(node.id)) continue;
      if (!rectsOverlap(bbox, rect)) continue;
      const len = intersectionLength(pts, rect);
      if (len <= min) continue;
      findings.push({
        kind: "routeThroughNode",
        subjects: [route.id, node.id],
        magnitude: len,
        at: intersectionAt(pts, rect),
      });
    }
  }
  return resultOf(findings);
}

// --- 3. proper crossings ---------------------------------------------------

const CROSSING_CLUSTER = 6;

/**
 * Two routes cutting each other strictly in their interiors. Routes that share
 * an endpoint node are exempt — they meet, which is the topology being drawn.
 *
 * Magnitude is `90 − crossing angle` in degrees: a perpendicular cut reads
 * cleanly and scores 0, a near-tangent graze is the one a reader mistakes for
 * a single line and scores near 90.
 */
export function properRouteCrossings(geo: SceneGeometry): PredicateResult {
  const geoms = routeGeoms(geo).filter((g) => g.bbox);
  const findings: Finding[] = [];
  for (let i = 0; i < geoms.length; i++) {
    for (let j = i + 1; j < geoms.length; j++) {
      const a = geoms[i]!;
      const b = geoms[j]!;
      if (!rectsOverlap(grow(a.bbox!, 1), grow(b.bbox!, 1))) continue;
      if (a.route.endpoints.some((e) => b.route.endpoints.includes(e))) continue;
      const hits: { at: Pt; sharpness: number; p: number; q: number }[] = [];
      for (let p = 1; p < a.pts.length; p++) {
        for (let q = 1; q < b.pts.length; q++) {
          const at = properIntersection(a.pts[p - 1]!, a.pts[p]!, b.pts[q - 1]!, b.pts[q]!);
          if (!at) continue;
          // One crossing can produce several segment-pair hits, because a
          // flattened curve crosses in ~24px steps. Cluster only ADJACENT
          // segment pairs, so two routes that genuinely cut each other twice
          // within a few px (a rail pair meeting a third route near a corner)
          // still count twice.
          if (
            hits.some(
              (h) =>
                Math.hypot(h.at.x - at.x, h.at.y - at.y) < CROSSING_CLUSTER &&
                Math.abs(h.p - p) <= 1 &&
                Math.abs(h.q - q) <= 1,
            )
          ) {
            continue;
          }
          const delta = headingDelta(
            heading(a.pts[p - 1]!, a.pts[p]!),
            heading(b.pts[q - 1]!, b.pts[q]!),
          );
          hits.push({ at, sharpness: 90 - (delta * 180) / Math.PI, p, q });
        }
      }
      for (const h of hits) {
        findings.push({
          kind: "properRouteCrossings",
          subjects: [a.route.id, b.route.id],
          magnitude: h.sharpness,
          at: h.at,
        });
      }
    }
  }
  return resultOf(findings);
}

// --- 4. ambiguous collinear corridor ---------------------------------------

/**
 * Two routes running alongside each other, near enough and long enough that a
 * reader cannot tell which is which. Measured on the SIMPLIFIED polylines: on
 * the raw one every run is one 3px curve sample long and nothing ever trips.
 *
 * Reported once per route pair, at its worst run.
 */
export function ambiguousCollinearCorridor(
  geo: SceneGeometry,
  opts: {
    readonly maxAngle?: number;
    readonly maxDistance?: number;
    readonly minOverlap?: number;
  } = {},
): PredicateResult {
  const maxAngle = opts.maxAngle ?? PARALLEL_ANGLE;
  const maxDistance = opts.maxDistance ?? AMBIGUOUS_DISTANCE;
  const minOverlap = opts.minOverlap ?? MIN_CORRIDOR_RUN;
  const geoms = routeGeoms(geo).filter((g) => g.bbox);
  const findings: Finding[] = [];
  for (let i = 0; i < geoms.length; i++) {
    for (let j = i + 1; j < geoms.length; j++) {
      const a = geoms[i]!;
      const b = geoms[j]!;
      if (!rectsOverlap(grow(a.bbox!, maxDistance), grow(b.bbox!, maxDistance))) continue;
      let worst: { overlap: number; at: Pt } | null = null;
      for (let p = 1; p < a.simple.length; p++) {
        const ha = heading(a.simple[p - 1]!, a.simple[p]!);
        for (let q = 1; q < b.simple.length; q++) {
          if (headingDelta(ha, heading(b.simple[q - 1]!, b.simple[q]!)) > maxAngle) continue;
          const run = parallelRun(a.simple[p - 1]!, a.simple[p]!, b.simple[q - 1]!, b.simple[q]!);
          if (!run || run.distance > maxDistance || run.overlap < minOverlap) continue;
          if (!worst || run.overlap > worst.overlap) worst = { overlap: run.overlap, at: run.at };
        }
      }
      if (worst) {
        findings.push({
          kind: "ambiguousCollinearCorridor",
          subjects: [a.route.id, b.route.id],
          magnitude: worst.overlap,
          at: worst.at,
        });
      }
    }
  }
  return resultOf(findings);
}

// --- 5. container border run -----------------------------------------------

/**
 * A route hugging a group frame's border. Crossing a border is normal (an edge
 * between two groups must); running ALONG one for `MIN_BORDER_RUN` px makes the
 * route disappear into the container's outline.
 */
export function containerBorderRun(
  geo: SceneGeometry,
  opts: { readonly tolerance?: number; readonly minOverlap?: number } = {},
): PredicateResult {
  const tolerance = opts.tolerance ?? BORDER_TOLERANCE;
  const minOverlap = opts.minOverlap ?? MIN_BORDER_RUN;
  const findings: Finding[] = [];
  const borders = geo.frames.map((f) => ({ frame: f, pts: rectBorder(f.rect) }));
  for (const { route, simple, bbox } of routeGeoms(geo)) {
    if (!bbox) continue;
    for (const { frame, pts } of borders) {
      if (!rectsOverlap(grow(bbox, tolerance), grow(frame.rect, tolerance))) continue;
      let worst: { overlap: number; at: Pt } | null = null;
      for (let p = 1; p < pts.length; p++) {
        const hb = heading(pts[p - 1]!, pts[p]!);
        for (let q = 1; q < simple.length; q++) {
          if (headingDelta(hb, heading(simple[q - 1]!, simple[q]!)) > PARALLEL_ANGLE) continue;
          const run = parallelRun(pts[p - 1]!, pts[p]!, simple[q - 1]!, simple[q]!);
          if (!run || run.distance > tolerance || run.overlap < minOverlap) continue;
          if (!worst || run.overlap > worst.overlap) worst = { overlap: run.overlap, at: run.at };
        }
      }
      if (worst) {
        findings.push({
          kind: "containerBorderRun",
          subjects: [route.id, frame.id],
          magnitude: worst.overlap,
          at: worst.at,
        });
      }
    }
  }
  return resultOf(findings);
}

// --- 6. route rhythm -------------------------------------------------------

/**
 * A route stuttering: a short hop sandwiched between two visible turns, which
 * reads as a kink rather than as a move. The rule itself is
 * {@link polylineStutters}; this walks the report's routes through it.
 */
export function routeRhythm(
  geo: SceneGeometry,
  opts: { readonly floor?: number; readonly kink?: number } = {},
): PredicateResult {
  const findings: Finding[] = [];
  for (const { route, pts } of routeGeoms(geo)) {
    for (const s of polylineStutters(pts, opts.floor ?? RHYTHM_FLOOR, opts.kink ?? RHYTHM_KINK)) {
      findings.push({
        kind: "routeRhythm",
        subjects: [route.id],
        magnitude: s.magnitude,
        at: s.at,
      });
    }
  }
  return resultOf(findings);
}

// --- 7. endpoint side direction --------------------------------------------

/** Cut a polyline to its first (or last) `length` px, interpolating the final
 *  point. Taking whole segments instead would make `ENDPOINT_PROBE` a lower
 *  bound, not a probe — a single `M … L …` route (which `orthogonalPath` and
 *  `bezierPath` both emit) would hand back its entire 500px length. */
function endSlice(pts: readonly Pt[], length: number, fromEnd: boolean): Pt[] {
  const seq = fromEnd ? [...pts].reverse() : pts;
  const out: Pt[] = seq.length ? [seq[0]!] : [];
  let acc = 0;
  for (let i = 1; i < seq.length; i++) {
    const a = seq[i - 1]!;
    const b = seq[i]!;
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    if (acc + len >= length) {
      const t = len < 1e-12 ? 1 : (length - acc) / len;
      out.push({ x: a.x + t * (b.x - a.x), y: a.y + t * (b.y - a.y) });
      return out;
    }
    out.push(b);
    acc += len;
  }
  return out;
}

/**
 * A route leaving on the wrong side: the first `ENDPOINT_PROBE` px dive back
 * into the very node the stroke just left (or the last px re-enter the target
 * before arriving). That is the "arrow points back at the node" defect
 * `portConnectorPath` documents — a control handle anchored to the wrong point
 * reverses the end tangent and the head lands inside its own box.
 *
 * The node rect is grown by 1px first so merely STARTING on the wall — which
 * every route does — is not itself a violation.
 */
export function endpointSideDirection(
  geo: SceneGeometry,
  opts: { readonly probe?: number; readonly minPenetration?: number } = {},
): PredicateResult {
  const probe = opts.probe ?? ENDPOINT_PROBE;
  const min = opts.minPenetration ?? MIN_PENETRATION;
  const rectById = new Map(geo.nodes.map((n) => [n.id, n.rect] as const));
  const findings: Finding[] = [];
  for (const { route, pts } of routeGeoms(geo)) {
    if (pts.length < 2) continue;
    for (const [id, fromEnd] of [
      [route.from, false],
      [route.to, true],
    ] as const) {
      if (!id) continue;
      const rect = rectById.get(id);
      if (!rect) continue;
      const inner = insetRect(rect, 1);
      const slice = endSlice(pts, probe, fromEnd);
      const len = intersectionLength(slice, inner);
      if (len <= min) continue;
      findings.push({
        kind: "endpointSideDirection",
        subjects: [route.id, id],
        magnitude: len,
        at: intersectionAt(slice, inner),
      });
    }
  }
  return resultOf(findings);
}

// --- 8. projected text floor -----------------------------------------------

/**
 * Text that will be illegible at the width the artifact is actually read at.
 * A board composed 3000px wide and shown 930px wide draws a 12px label at
 * under 4px; sizing against the composed width alone hides that entirely.
 *
 * Magnitude is px below the floor.
 */
export function projectedTextFloor(
  geo: SceneGeometry,
  opts: { readonly referenceWidth?: number; readonly floorPx?: number } = {},
): PredicateResult {
  const referenceWidth = opts.referenceWidth ?? REFERENCE_WIDTH;
  const floorPx = opts.floorPx ?? TEXT_FLOOR_PX;
  const scale = Math.min(1, referenceWidth / Math.max(1, geo.bounds.w));
  const findings: Finding[] = [];
  for (const t of geo.texts) {
    const projected = t.fontSize * scale;
    if (projected >= floorPx) continue;
    findings.push({
      kind: "projectedTextFloor",
      // The text itself trails the owner id — a font-floor finding is useless
      // without knowing WHICH label is illegible.
      subjects: [t.ownerId, t.text],
      magnitude: floorPx - projected,
      at: { x: t.rect.x + t.rect.w / 2, y: t.rect.y + t.rect.h / 2 },
    });
  }
  return resultOf(findings);
}
